import type { OrderCostPlan, OrderGoodsCostBreakdown } from '../../../../types/orderWarehouseSummary';

/**
 * ZK „Wyjaśnienie marży”: revenue minus each cost. The goods cost (COGS) is
 * always shown as three parts — towar, transport, koszty dodatkowe — split by
 * the backend (`plan.goodsBreakdown`, orderGoodsCostBreakdown.js; decision
 * 2026-10-07). Nothing is re-split here: a payload without the split shows the
 * goods cost as one unsplit part.
 */
export function goodsBreakdown(plan: OrderCostPlan): OrderGoodsCostBreakdown {
  if (plan.goodsBreakdown) return plan.goodsBreakdown;
  return {
    purchasePLN: 0, inboundFreightPLN: 0, landedPLN: 0, extraUnsplitPLN: 0,
    averagePLN: plan.averageCostPLN, unsplitPLN: plan.issuedCostPLN + plan.stockCostPLN,
    plannedFreightPLN: plan.plannedFreightPLN ?? 0,
  };
}

/**
 * goodsItem = purchase from batches + shortfall at the average purchase cost;
 * goodsTransport = inbound freight in batches + planned truck freight until
 * the PZ; goodsExtra = duty and other landed costs. extraUnsplit / unsplit are
 * older batches without a split (shown only when present).
 */
export type RevenueShareKey = 'goodsItem' | 'goodsTransport' | 'goodsExtra' | 'extraUnsplit' | 'unsplit'
  | 'outboundTransport' | 'other' | 'margin';

export const GOODS_SHARE_KEYS: RevenueShareKey[] = ['goodsItem', 'goodsTransport', 'goodsExtra', 'extraUnsplit', 'unsplit'];
/** Parts of the goods cost shown even at 0, so the reader sees what COGS consists of. */
const ALWAYS_SHOWN: RevenueShareKey[] = ['goodsItem', 'goodsTransport', 'goodsExtra'];

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Where the „Towar” part comes from: batches, the average purchase cost or both. */
export function goodsItemSource(breakdown: OrderGoodsCostBreakdown): 'batch' | 'average' | 'mixed' {
  if (breakdown.averagePLN > 0 && breakdown.purchasePLN > 0) return 'mixed';
  return breakdown.averagePLN > 0 ? 'average' : 'batch';
}

/** Transport inside the goods cost: freight in the batches + planned truck freight before the PZ. */
export function goodsTransportPLN(breakdown: OrderGoodsCostBreakdown | undefined | null): number {
  if (!breakdown) return 0;
  return round2(breakdown.inboundFreightPLN + (breakdown.plannedFreightPLN ?? 0));
}

export interface RevenueShare {
  key: RevenueShareKey;
  amountPLN: number;
  /** Share of revenue, %. */
  percent: number;
  /** Bar width, 0–1 (costs above revenue are scaled to fit). */
  width: number;
}

/** Where each złoty of revenue goes; null when revenue or margin is unknown. */
export function revenueShares(input: {
  revenuePLN: number | null | undefined;
  marginPLN: number | null | undefined;
  breakdown: OrderGoodsCostBreakdown;
  transportPLN: number;
  otherPLN: number;
}): RevenueShare[] | null {
  const { revenuePLN: revenue, marginPLN: margin, breakdown } = input;
  if (revenue == null || revenue <= 0 || margin == null) return null;
  const parts: { key: RevenueShareKey; amountPLN: number }[] = [
    { key: 'goodsItem', amountPLN: round2(breakdown.purchasePLN + breakdown.averagePLN) },
    { key: 'goodsTransport', amountPLN: goodsTransportPLN(breakdown) },
    { key: 'goodsExtra', amountPLN: breakdown.landedPLN },
    { key: 'extraUnsplit', amountPLN: breakdown.extraUnsplitPLN },
    { key: 'unsplit', amountPLN: breakdown.unsplitPLN },
    { key: 'outboundTransport', amountPLN: input.transportPLN },
    { key: 'other', amountPLN: input.otherPLN },
    { key: 'margin', amountPLN: margin },
  ];
  // A negative part (a credit among other costs, a loss) stays in the list so
  // the rows still add up; it just has no bar.
  const base = Math.max(revenue, parts.reduce((sum, part) => sum + Math.max(0, part.amountPLN), 0));
  return parts.filter(part => part.amountPLN !== 0 || ALWAYS_SHOWN.includes(part.key)).map(part => ({
    ...part,
    percent: Math.round(part.amountPLN / revenue * 1000) / 10,
    width: Math.max(0, part.amountPLN) / base,
  }));
}

export type SplitUnit = 'total' | 'pcs' | 'm2' | 'm3';

type QuantityLine = { quantityPcs?: number | null; quantityM2?: number | null; quantityM3?: number | null };
const UNIT_FIELDS = [['pcs', 'quantityPcs'], ['m2', 'quantityM2'], ['m3', 'quantityM3']] as const;

/**
 * Order quantity per unit for the „na szt. / m² / m³” switch: a unit exists only
 * when every line has a positive stored equivalent (a window has no m³).
 */
export function orderQuantityTotals(lines: QuantityLine[]): Partial<Record<Exclude<SplitUnit, 'total'>, number>> {
  if (lines.length === 0) return {};
  const totals: Partial<Record<Exclude<SplitUnit, 'total'>, number>> = {};
  UNIT_FIELDS.forEach(([unit, field]) => {
    const values = lines.map((line) => line[field]);
    if (values.every((value) => typeof value === 'number' && Number.isFinite(value) && value > 0)) {
      totals[unit] = values.reduce((sum: number, value) => sum + (value as number), 0);
    }
  });
  return totals;
}
